import type {
  Die,
  DieId,
  SinChoice,
  SinType,
} from "@cloudnest/carnival-of-sins-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import { CarnivalTable } from "./CarnivalTable";
import { SIN_LABELS } from "./event-summary";
import {
  carnivalOfSinsMultiplayerActions,
  useCarnivalOfSinsMultiplayerStore,
} from "./multiplayer-game-store";
import { ScorePad } from "./ScorePad";
import "./styles/carnival-of-sins-tokens.css";
import "./styles/carnival-of-sins-client.css";

/** How many dice this sin's reveal needs the player to click before submitting. */
const REQUIRED_PICKS: Readonly<Record<SinType, number>> = {
  greed: 2,
  gluttony: 1,
  envy: 0,
  lust: 2,
  pride: 0,
  wrath: 1,
  sloth: 0,
};

/** Sins whose reveal has a legitimate "nothing to take" outcome (server validates it's actually true). */
const SUPPORTS_TAKE_NONE: ReadonlySet<SinType> = new Set([
  "greed",
  "gluttony",
  "lust",
]);

/** Matches the wireframe's cos-sin-card glyphs (docs/wireframes/carnival-of-sins/03-screen.html). */
const SIN_GLYPH: Readonly<Record<SinType, string>> = {
  greed: "🪙",
  gluttony: "🍖",
  envy: "😒",
  lust: "💋",
  pride: "👑",
  wrath: "🔥",
  sloth: "🛌",
};

/** Short English name for the compact sin card (SIN_LABELS' bilingual text doesn't fit a 74px card). */
function sinShortName(sin: SinType): string {
  return SIN_LABELS[sin].split(" · ")[0] ?? SIN_LABELS[sin];
}

function buildChoice(
  sin: SinType,
  dieIds: readonly DieId[],
  dice: readonly Die[],
): SinChoice | null {
  const dieById = new Map(dice.map((d) => [d.id, d]));
  const pickOf = (id: DieId) => {
    const die = dieById.get(id);
    return die?.lockedBy
      ? { dieId: id, stolenFromPlayerId: die.lockedBy.ownerId }
      : { dieId: id };
  };
  switch (sin) {
    case "greed":
      return dieIds.length === 2
        ? { sin, dieIds: [dieIds[0], dieIds[1]] }
        : null;
    case "gluttony":
      return dieIds.length === 1 ? { sin, dieId: dieIds[0] } : null;
    case "lust":
      return dieIds.length === 2
        ? { sin, picks: [pickOf(dieIds[0]), pickOf(dieIds[1])] }
        : null;
    case "wrath": {
      if (dieIds.length !== 1) return null;
      const die = dieById.get(dieIds[0]);
      return {
        sin,
        target: die?.lockedBy
          ? { dieId: dieIds[0], fromPlayerId: die.lockedBy.ownerId }
          : { dieId: dieIds[0] },
      };
    }
    case "envy":
    case "pride":
    case "sloth":
      return { sin };
  }
}

function takeNoneChoice(sin: SinType): SinChoice | null {
  switch (sin) {
    case "greed":
      return { sin, dieIds: null };
    case "gluttony":
      return { sin, dieId: null };
    case "lust":
      return { sin, picks: null };
    default:
      return null;
  }
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, mySlot } =
    useCarnivalOfSinsMultiplayerStore();
  const actions = carnivalOfSinsMultiplayerActions;

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const participantIds = useMemo(
    () => currentRoom?.participantUserIds ?? [],
    [currentRoom?.participantUserIds],
  );
  const isObserver = Boolean(user) && !participantIds.includes(user?.id ?? "");

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useGameEventsWS(roomId, {
    onGameEvent: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id + role are the stable init boundaries.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    if (isObserver) {
      void actions.initializeAsObserver(roomId);
    } else if (participantIds.length >= 2) {
      if (isHost) void actions.initializeAsHost(roomId, user.id);
      else void actions.initializeAsGuest(roomId, user.id);
    }
    return () => {
      actions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost, isObserver]);

  const [picking, setPicking] = useState<readonly DieId[]>([]);

  const state = viewerState;
  const isMyRevealTurn =
    state?.phase === "REVEAL_RESOLVE" &&
    mySlot !== null &&
    state.revealOrder[state.nextToRevealIndex] === mySlot;
  const pendingSin = isMyRevealTurn
    ? (state?.ownPendingSelection ?? null)
    : null;

  // Reset transient dice picks whenever the server-projected reveal turn moves.
  const revealTurnKey = `${state?.round ?? 0}:${state?.nextToRevealIndex ?? 0}:${state?.phase ?? "-"}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on the turn boundary only.
  useEffect(() => {
    setPicking([]);
  }, [revealTurnKey]);

  if (!currentRoom) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">방을 찾을 수 없습니다</h1>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            로비로
          </button>
        </section>
      </div>
    );
  }

  if (!state) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface-primary text-text-secondary"
        data-testid="carnival-of-sins-game-loading"
      >
        연결 중…
      </div>
    );
  }

  const isGameOver = state.phase === "GAME_OVER";
  const displayPlayers = state.players.map((p) => ({
    playerId: p.playerId,
    name: getParticipantDisplayName(currentRoom, p.playerId),
  }));
  const namesByPlayerId = Object.fromEntries(
    displayPlayers.map((p) => [p.playerId, p.name]),
  );
  const devilName =
    namesByPlayerId[state.devilCardHolderId] ?? state.devilCardHolderId;
  const winnerNames = (state.winnerIds ?? [])
    .map((id) => namesByPlayerId[id] ?? id)
    .join(" & ");

  // ── dice picking for the four sins with a real target choice ────────────
  const requiredPicks = pendingSin ? REQUIRED_PICKS[pendingSin] : 0;
  const blackDice = state.dice.filter((d) => d.color === "black");
  const unlockedBlack = blackDice.filter((d) => d.lockedBy === null);
  let selectableDieIds: ReadonlySet<DieId> = new Set();
  if (pendingSin && requiredPicks > 0 && picking.length < requiredPicks) {
    if (pendingSin === "greed" || pendingSin === "gluttony") {
      selectableDieIds = new Set(unlockedBlack.map((d) => d.id));
    } else if (pendingSin === "lust" || pendingSin === "wrath") {
      selectableDieIds = new Set(blackDice.map((d) => d.id));
    }
  }
  const selectedDieIds = new Set(picking);

  function onDieClick(die: Die): void {
    setPicking((prev) => {
      if (prev.includes(die.id)) return prev.filter((id) => id !== die.id);
      if (requiredPicks === 0 || prev.length >= requiredPicks) return prev;
      return [...prev, die.id];
    });
  }

  function submitReveal(choice: SinChoice | null): void {
    if (!choice) return;
    void actions.revealSin(choice);
    setPicking([]);
  }

  const canSubmitPicks =
    pendingSin !== null &&
    picking.length === requiredPicks &&
    requiredPicks > 0;

  return (
    <div
      data-testid="game-screen"
      className="min-h-screen px-3 py-4 sm:px-6"
      style={{ background: "var(--cn-carnival-of-sins-bg)" }}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3">
        <div className="flex items-center justify-between">
          <h1 className="m-0 font-bold text-lg text-text-primary">
            Carnival of Sins
          </h1>
          <button
            type="button"
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
            onClick={() => navigateTo("/")}
          >
            ‹ 로비
          </button>
        </div>

        {isGameOver ? (
          <div
            data-testid="carnival-of-sins-result"
            className="rounded-md border border-accent-blue-light bg-accent-blue-bg px-4 py-3 text-center font-semibold text-accent-blue text-sm"
          >
            👑 {winnerNames || "무승부"} — Monarch of the Carnival
          </div>
        ) : (
          <div
            data-testid="carnival-of-sins-status-bar"
            className="rounded-md border border-border-subtle bg-surface-secondary px-4 py-2 text-center text-sm text-text-secondary"
          >
            라운드 <span className="font-semibold">{state.round}/7</span> ·{" "}
            {state.phase === "SECRET_SELECT" ? "비밀 선택" : "공개 · 해결"} ·
            데빌 카드 <span className="font-semibold">{devilName}</span>
          </div>
        )}

        <div className="flex justify-center">
          <CarnivalTable
            players={displayPlayers}
            seatingOrder={state.seatingOrder}
            devilCardHolderId={state.devilCardHolderId}
            activePlayerId={
              state.phase === "REVEAL_RESOLVE"
                ? state.revealOrder[state.nextToRevealIndex]
                : null
            }
            dice={state.dice}
            onDieClick={onDieClick}
            selectableDieIds={selectableDieIds}
            selectedDieIds={selectedDieIds}
          />
        </div>

        <ScorePad
          players={displayPlayers}
          scoreHistoryByPlayerId={state.scoreHistoryByPlayerId}
          winnerIds={state.winnerIds}
        />

        <div
          data-testid="carnival-of-sins-action-dock"
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-secondary p-3"
        >
          {isGameOver ? (
            <div className="text-center text-sm text-text-secondary">
              게임이 종료되었습니다.
            </div>
          ) : isObserver ? (
            <div className="text-center text-sm text-text-secondary">
              관전 중 · 행동 없음
            </div>
          ) : state.phase === "SECRET_SELECT" ? (
            state.ownPendingSelection === null ? (
              <>
                <div className="text-center text-text-secondary text-xs">
                  Sin 카드를 하나 선택하세요 (
                  {state.playersWhoSelectedThisRound.length}/
                  {state.players.length} 완료)
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {state.ownHand.map((sin) => (
                    <button
                      key={sin}
                      type="button"
                      data-testid={`carnival-of-sins-select-${sin}`}
                      className={`cos-sin-card sin-${sin}`}
                      onClick={() => void actions.selectSin(sin)}
                    >
                      <span className="glyph">{SIN_GLYPH[sin]}</span>
                      <span className="name">{sinShortName(sin)}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center text-sm text-text-secondary">
                선택 완료 · {state.playersWhoSelectedThisRound.length}/
                {state.players.length}명 대기 중
              </div>
            )
          ) : isMyRevealTurn && pendingSin ? (
            <>
              <div className="text-center text-text-secondary text-xs">
                {SIN_LABELS[pendingSin]} 공개
                {requiredPicks > 0
                  ? ` · 주사위 ${picking.length}/${requiredPicks} 선택`
                  : ""}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {requiredPicks === 0 ? (
                  <button
                    type="button"
                    data-testid="carnival-of-sins-reveal-submit"
                    className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white"
                    onClick={() =>
                      submitReveal(buildChoice(pendingSin, [], state.dice))
                    }
                  >
                    공개하기
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="carnival-of-sins-reveal-submit"
                      disabled={!canSubmitPicks}
                      className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white disabled:opacity-40"
                      onClick={() =>
                        submitReveal(
                          buildChoice(pendingSin, picking, state.dice),
                        )
                      }
                    >
                      제출
                    </button>
                    {picking.length > 0 ? (
                      <button
                        type="button"
                        className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm"
                        onClick={() => setPicking([])}
                      >
                        다시 선택
                      </button>
                    ) : null}
                    {SUPPORTS_TAKE_NONE.has(pendingSin) ? (
                      <button
                        type="button"
                        data-testid="carnival-of-sins-take-none"
                        className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm"
                        onClick={() => submitReveal(takeNoneChoice(pendingSin))}
                      >
                        가져갈 것 없음
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-sm text-text-secondary">
              {namesByPlayerId[state.revealOrder[state.nextToRevealIndex]] ??
                "상대"}{" "}
              공개를 기다리는 중…
            </div>
          )}
        </div>

        {lastError ? (
          <div
            className="rounded-md border border-accent-red bg-surface-secondary p-2 text-accent-red text-xs"
            data-testid="carnival-of-sins-error"
          >
            {lastError.error}
            <button
              type="button"
              className="ml-2 underline"
              onClick={() => actions.clearError()}
            >
              닫기
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
